import { useEffect } from 'react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { fDateTimeByLanguage } from '@zep/utils';
import { CreditCardList } from '../../../../consts';
import { useSingularSubscriptionPaymentResult } from '../../../../services';
import { SingularSubscriptionPaymentResponse } from '../../../../types/PaymentTypes';
import { BusinessPaymentLayout } from '../../business/BusinessPaymentLayout';
import { BusinessPaymentInvalidPage } from '../../business/BusinessPayments';
import S from '../../business/BusinessPayments.module.scss';
import { SuccessSingularPaymentInformation } from './components/SuccessSingularPaymentInformation';
const SingularSubscriptionSuccessContent = () => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['business']);
  const paymentRequestHashId = (router.query.id ?? '').toString();
  const email = (router.query.email ?? '').toString();
  const {
    data: paymentInformation,
    isFetched
  } = useSingularSubscriptionPaymentResult({
    paymentRequestHashId,
    email
  });
  const paymentTime = new Date(paymentInformation?.payment?.approvedAt ?? NaN);
  const productName = t('business:businessPage.productType.premium');
  useEffect(() => {
    logger.track('payment_business_complete', {
      product_type: 'premium_plan',
      currency: paymentInformation?.spacePlan.currency,
      // revenue는 persisted 실결제액(payment.totalPrice, VAT 포함) 기준.
      // salePrice(VAT 전)는 결제 이력이 없는 비정상 케이스의 fallback일 뿐이다.
      $revenue: (paymentInformation?.payment?.totalPrice ?? paymentInformation?.spacePlan.salePrice ?? 0) * (paymentInformation?.spacePlan.currency === 'KRW' ? 1 : 1200)
    });
  }, [paymentInformation]);
  if (!isFetched) {
    return <></>;
  }
  // 조회 실패면 rejectedFallback(잘못된 결제 페이지)으로 보낸다.
  if (!paymentInformation) {
    throw new Error('singular payment result not found');
  }
  return <BusinessPaymentLayout isCenter={false} title={paymentInformation.paymentRequest.customerName} data-sentry-element="BusinessPaymentLayout" data-sentry-component="SingularSubscriptionSuccessContent" data-sentry-source-file="SingularPaymentsSuccess.tsx">
      <div className={S.payments_result_section}>
        <h2 className={S.payments_result_title}>
          {t('business:business_payments_complete')}
        </h2>
        <div>
          {t('business:business_payments_complete_content', {
          name: paymentInformation?.paymentRequest.customerName
        })}
        </div>
        <div>
          {t('business:business_payments_complete_confirmation_number')}
          &nbsp;:{' '}
          <span>
            {paymentInformation.orderId || paymentInformation.payment?.orderId}
          </span>
        </div>
        <div className={S.payments_result_label}>
          {paymentInformation?.spacePlan.currency === 'KRW' ? <ResultKRWLabel paymentTime={paymentTime} paymentInformation={{
          ...paymentInformation
        }} /> : <ResultUSDLabel paymentTime={paymentTime} payment={paymentInformation} />}
        </div>
      </div>
      <SuccessSingularPaymentInformation customerName={paymentInformation?.paymentRequest.customerName} productName={productName} paymentInformation={paymentInformation} data-sentry-element="SuccessSingularPaymentInformation" data-sentry-source-file="SingularPaymentsSuccess.tsx" />
    </BusinessPaymentLayout>;
};
const ResultKRWLabel = ({
  paymentInformation,
  paymentTime
}: {
  paymentInformation: SingularSubscriptionPaymentResponse;
  paymentTime: Date;
}) => {
  const {
    i18n,
    t
  } = useTranslation(['subscription']);
  const cardName = paymentInformation.payment?.cardCompanyCode && CreditCardList[paymentInformation.payment.cardCompanyCode] || '';
  const paymentMethod = paymentInformation.paymentRequest.paymentType === 'CARD' ? t('subscription:onetimeInvoice.paymentMethod.card') : t('subscription:onetimeInvoice.paymentMethod.transfer');
  const isValidPaymentTime = !isNaN(paymentTime.getTime());
  return <>
      {paymentMethod}
      {isValidPaymentTime && ` | ${fDateTimeByLanguage(paymentTime, i18n.resolvedLanguage ?? '')}`}
      {cardName && ` | ${cardName}`}
    </>;
};
const ResultUSDLabel = ({
  paymentTime
}: {
  payment: SingularSubscriptionPaymentResponse;
  paymentTime: Date;
}) => {
  const {
    i18n
  } = useTranslation();
  const isValidPaymentTime = !isNaN(paymentTime.getTime());
  return <>
      {isValidPaymentTime && fDateTimeByLanguage(paymentTime, i18n.resolvedLanguage ?? '') || ''}
    </>;
};
export const SingularPaymentsSuccess = withAsyncBoundary(SingularSubscriptionSuccessContent, {
  pendingFallback: '',
  // 결제는 끝났는데 결과 화면을 못 띄운 경우라 조용히 넘기지 않는다.
  onError: error => logger.captureError(error, {
    level: 'warning',
    tags: {
      source: 'singular-payment-success'
    }
  }),
  rejectedFallback: () => {
    return <BusinessPaymentInvalidPage />;
  }
});